import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { Box, Image, VStack, Text, Flex, Heading } from "@chakra-ui/react";
import { TEXTS } from "../constants/texts";
import StoryImage from "../images/Menu/Story.png";
import TGM1 from "../images/Assets/TGM1.jpg";
import TGM2 from "../images/Assets/TGM2.jpg";
import TGM3 from "../images/Assets/TGM3.jpg";
import BlueHaikei from "../images/Background/BlueHaikei.jpg";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";

// 共通プロパティの型定義
interface CommonImageProps {
  onContextMenu: (e: { preventDefault: () => void }) => void;
  draggable: boolean;
}

// イベントプリベント共通関数
const preventDefault = (e: { preventDefault: () => void }): void => {
  e.preventDefault();
};

const commonImageProps: CommonImageProps = {
  onContextMenu: preventDefault,
  draggable: false,
};

// アニメーション状態の型定義
type AnimationState = "hidden" | "visible" | "instant";

const Story = () => {
  const tgmImages = useMemo(
    () => [
      { src: TGM1, alt: "TGM1" },
      { src: TGM2, alt: "TGM2" },
      { src: TGM3, alt: "TGM3" },
    ],
    []
  );

  // スクロール方向の検出
  const prevScrollY = useRef(0);
  const storyRef = useRef<HTMLDivElement>(null);
  const [scrollDirection, setScrollDirection] = useState<"down" | "up">("down");
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const scrollTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastScrollTimeRef = useRef(0);

  // すべてのアニメーション状態を一元管理
  const [animationStates, setAnimationStates] = useState<
    Record<string, AnimationState>
  >({
    title: "hidden",
    content: "hidden",
    bottomContent: "hidden",
    storyImage: "hidden",
    text: "hidden",
    tgmImage1: "hidden",
    tgmImage2: "hidden",
    tgmImage3: "hidden",
    lastText: "hidden",
  });

  // アニメーション状態を更新する共通関数
  const updateAnimationState = useCallback(
    (key: string, isInView: boolean) => {
      if (isInView) {
        requestAnimationFrame(() => {
          setAnimationStates((prev) => {
            const current = prev[key];
            if (scrollDirection === "up") {
              // Preserve visibility when scrolling up; jump to final state
              return current === "visible" || current === "instant"
                ? prev
                : { ...prev, [key]: "instant" };
            }
            // When scrolling down, only retrigger if currently hidden
            if (current === "visible" || current === "instant") return prev;
            return { ...prev, [key]: "visible" };
          });
        });
      } else {
        // Do not force hidden on upward scroll to avoid flicker
        if (scrollDirection === "down") {
          setAnimationStates((prev) => ({ ...prev, [key]: "hidden" }));
        }
      }
    },
    [scrollDirection]
  );

  // useInView設定の最適化
  const useInViewConfig = useMemo(
    () => ({
      triggerOnce: false,
      threshold: 0.2,
      rootMargin: "10% 0px -10% 0px",
    }),
    []
  );

  // useInViewフック
  const { ref: componentRef } = useInView(useInViewConfig);
  const { ref: titleRef, inView: titleInView } = useInView(useInViewConfig);
  const { ref: contentRef, inView: contentInView } = useInView(useInViewConfig);
  const { ref: bottomContentRef, inView: bottomContentInView } =
    useInView(useInViewConfig);
  const { ref: storyImageRef, inView: storyImageInView } =
    useInView(useInViewConfig);
  const { ref: textRef, inView: textInView } = useInView(useInViewConfig);
  const { ref: tgmImage1Ref, inView: tgmImage1InView } =
    useInView(useInViewConfig);
  const { ref: tgmImage2Ref, inView: tgmImage2InView } =
    useInView(useInViewConfig);
  const { ref: tgmImage3Ref, inView: tgmImage3InView } =
    useInView(useInViewConfig);
  const { ref: lastTextRef, inView: lastTextInView } =
    useInView(useInViewConfig);

  // スクロール方向検出
  useEffect(() => {
    // 初期スクロール位置を設定
    prevScrollY.current = window.scrollY;

    // モバイルデバイス検出
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    const throttleTime = isMobile ? 16 : 50; // モバイルではより高速に反応（約60fps）

    const handleScroll = () => {
      // スロットリング実装
      const now = Date.now();
      if (now - lastScrollTimeRef.current < throttleTime) {
        // 前回のスクロール処理からthrottleTime経っていない場合はスキップ
        if (scrollTimerRef.current === null) {
          // 次回の処理をスケジュール
          scrollTimerRef.current = setTimeout(() => {
            lastScrollTimeRef.current = Date.now();
            scrollTimerRef.current = null;
            handleScroll();
          }, throttleTime);
        }
        return;
      }

      lastScrollTimeRef.current = now;
      const currentScrollY = window.scrollY;
      const previousY = prevScrollY.current;
      const diff = currentScrollY - previousY;
      const isScrollingDown = diff > 0;

      // スクロール方向を更新（小さな変動は無視）
      if (Math.abs(diff) > 2) {
        const newDirection = isScrollingDown ? "down" : "up";

        // 方向が変わった場合、または下スクロールの場合は常に状態を更新
        if (newDirection !== scrollDirection || newDirection === "down") {
          setScrollDirection(newDirection);
        }
      }

      prevScrollY.current = currentScrollY;
    };

    // スクロールイベントをより頻繁に検出
    window.addEventListener("scroll", handleScroll, { passive: true });

    // タッチイベントも監視（モバイル向け）
    if (isMobile) {
      let touchStartY = 0;

      const handleTouchStart = (e: TouchEvent) => {
        touchStartY = e.touches[0].clientY;
      };

      const handleTouchMove = (e: TouchEvent) => {
        const touchY = e.touches[0].clientY;
        const diff = touchStartY - touchY;

        // 明確なスワイプを検出した場合のみ方向を更新
        if (Math.abs(diff) > 10) {
          setScrollDirection(diff > 0 ? "down" : "up");
          touchStartY = touchY;
        }
      };

      window.addEventListener("touchstart", handleTouchStart, {
        passive: true,
      });
      window.addEventListener("touchmove", handleTouchMove, { passive: true });

      return () => {
        window.removeEventListener("scroll", handleScroll);
        window.removeEventListener("touchstart", handleTouchStart);
        window.removeEventListener("touchmove", handleTouchMove);
        if (scrollTimerRef.current) {
          clearTimeout(scrollTimerRef.current);
        }
      };
    }

    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (scrollTimerRef.current) {
        clearTimeout(scrollTimerRef.current);
      }
    };
  }, [scrollDirection]);

  // 初期ロード時の表示要素を検出
  useEffect(() => {
    if (isInitialLoad) {
      const handleInitialVisibility = () => {
        const scrollY = window.scrollY;
        const viewportHeight = window.innerHeight;
        const viewportBottom = scrollY + viewportHeight;

        if (!storyRef.current) return;

        const storyRect = storyRef.current.getBoundingClientRect();
        const storyTop = storyRect.top + scrollY;
        const storyHeight = storyRect.height;

        const isVisible =
          storyTop < viewportBottom && storyTop + storyHeight > scrollY;

        if (isVisible) {
          // 初期表示時には全てのアニメーション状態を "visible" に設定
          setAnimationStates({
            title: "visible",
            content: "visible",
            bottomContent: "visible",
            storyImage: "visible",
            text: "visible",
            tgmImage1: "visible",
            tgmImage2: "visible",
            tgmImage3: "visible",
            lastText: "visible",
          });
        }

        setIsInitialLoad(false);
      };

      if (document.readyState === "complete") {
        handleInitialVisibility();
      } else {
        window.addEventListener("load", handleInitialVisibility, {
          once: true,
        });
        return () =>
          window.removeEventListener("load", handleInitialVisibility);
      }
    }
  }, [isInitialLoad]);

  // 各要素のInView状態を監視して、アニメーション状態を更新
  useEffect(() => {
    updateAnimationState("title", titleInView);
  }, [titleInView, updateAnimationState]);

  useEffect(() => {
    updateAnimationState("content", contentInView);
  }, [contentInView, updateAnimationState]);

  useEffect(() => {
    updateAnimationState("bottomContent", bottomContentInView);
  }, [bottomContentInView, updateAnimationState]);

  useEffect(() => {
    updateAnimationState("storyImage", storyImageInView);
  }, [storyImageInView, updateAnimationState]);

  useEffect(() => {
    updateAnimationState("text", textInView);
  }, [textInView, updateAnimationState]);

  useEffect(() => {
    updateAnimationState("tgmImage1", tgmImage1InView);
  }, [tgmImage1InView, updateAnimationState]);

  useEffect(() => {
    updateAnimationState("tgmImage2", tgmImage2InView);
  }, [tgmImage2InView, updateAnimationState]);

  useEffect(() => {
    updateAnimationState("tgmImage3", tgmImage3InView);
  }, [tgmImage3InView, updateAnimationState]);

  useEffect(() => {
    updateAnimationState("lastText", lastTextInView);
  }, [lastTextInView, updateAnimationState]);

  // スライドインアニメーション設定
  const slideInVariants = {
    hidden: {
      opacity: 0,
      y: 20,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut",
      },
    },
    instant: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0,
        ease: "easeOut",
      },
    },
  };

  // テキスト専用のフェードインアニメーション
  const textFadeInVariants = {
    hidden: {
      opacity: 0,
    },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut",
      },
    },
    instant: {
      opacity: 1,
      transition: {
        duration: 0,
        ease: "easeOut",
      },
    },
  };

  // TGM画像のアニメーション設定
  const getImageAnimationDelay = useCallback(
    (index: number) => {
      return scrollDirection === "down" ? index * 0.3 : 0;
    },
    [scrollDirection]
  );

  // 要素を参照で結合する関数
  const combineRefs = useCallback(
    (element: HTMLDivElement | null) => {
      if (element) {
        storyRef.current = element;
        if (typeof componentRef === "function") {
          componentRef(element);
        }
      }
    },
    [componentRef]
  );

  // TGM画像のアニメーション状態ヘルパー
  const getTgmImageAnimationState = useCallback(
    (index: number) => {
      if (index === 0) return animationStates.tgmImage1;
      if (index === 1) return animationStates.tgmImage2;
      return animationStates.tgmImage3;
    },
    [animationStates]
  );

  return (
    <Box
      position="relative"
      w="100%"
      ref={combineRefs}
      sx={{ willChange: "transform, opacity", backfaceVisibility: "hidden" }}
    >
      {/* 背景部分 */}
      <Box position="relative">
        {/* 上部の背景画像 */}
        <Image
          src={BlueHaikei}
          alt="BlueHaikei"
          w="100%"
          h="100%"
          minH="100vh"
          objectFit="cover"
          position="absolute"
          top="0"
          left="0"
          right="0"
          zIndex="-1"
        />

        {/* コンテンツ部分 */}
        <Box
          width="100%"
          py={{
            base: "80px",
            sm: "100px",
            md: "120px",
            lg: "140px",
          }}
          px={{
            base: "16px",
            sm: "24px",
            md: "32px",
            lg: "40px",
          }}
        >
          {/* タイトルとコンテンツをまとめるコンテナ */}
          <Box
            position="relative"
            top={{
              base: "-60px",
              sm: "-70px",
              md: "-80px",
              lg: "-90px",
              xl: "-100px",
              "2xl": "-50px",
            }}
            width="100%"
          >
            {/* タイトル部分 */}
            <Box mb={0}>
              <Heading
                id="about-title-anchor"
                scrollMarginTop={{
                  base: "0px",
                  sm: "0px",
                  md: "80px",
                  lg: "80px",
                  xl: "80px",
                  "2xl": "100px",
                }}
                position="relative"
                color="white"
                fontSize={{
                  base: "13px",
                  sm: "15px",
                  md: "20px",
                  lg: "30px",
                  xl: "32px",
                  "2xl": "36px",
                }}
                textAlign="center"
                px={{
                  base: 4,
                  sm: 6,
                  md: 8,
                }}
                py={{
                  base: 2,
                  sm: 2,
                  md: 2,
                  lg: 3,
                }}
                width={{
                  base: "80%",
                  sm: "83%",
                  md: "70%",
                  lg: "75%",
                  xl: "65%",
                  "2xl": "50%",
                }}
                mx="auto"
                sx={{
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    left: 0,
                    top: "50%",
                    transform: "translateY(-50%)",
                    width: "40%",
                    height: {
                      base: "2px",
                      sm: "2px",
                      md: "2px",
                      lg: "3px",
                      xl: "3px",
                      "2xl": "3px",
                    },
                    background: "white",
                  },
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    right: 0,
                    top: "50%",
                    transform: "translateY(-50%)",
                    width: "40%",
                    height: {
                      base: "2px",
                      sm: "2px",
                      md: "2px",
                      lg: "3px",
                      xl: "3px",
                      "2xl": "3px",
                    },
                    background: "white",
                  },
                }}
              >
                <motion.div
                  ref={titleRef}
                  initial="hidden"
                  animate={animationStates.title}
                  variants={{
                    hidden: { opacity: 0, y: 20 },
                    visible: {
                      opacity: 1,
                      y: 0,
                      transition: {
                        duration: 0.5, // 0.8から0.5に持続時間を短くする
                        ease: "easeOut",
                      },
                    },
                    instant: {
                      opacity: 1,
                      y: 0,
                      transition: {
                        duration: 0,
                        ease: "easeOut",
                      },
                    },
                  }}
                >
                  {TEXTS.Story.title}
                </motion.div>
              </Heading>
            </Box>

            {/* メインコンテンツコンテナ */}
            <VStack
              spacing={{
                base: "30px",
                sm: "40px",
                md: "50px",
                lg: "60px",
              }}
              align="center"
              width="100%"
            >
              {/* コンテンツ上部 - 強調テキスト部分 */}
              <Box
                bg="rgba(255, 197, 150, 0.1)"
                borderRadius="md"
                position="relative"
                top={{
                  base: "-17px",
                  sm: "-20px",
                  md: "-17px",
                  lg: "-33px",
                  xl: "-35px",
                  "2xl": "-35px",
                }}
                py={0}
                px={0}
                minHeight={{
                  base: "100px",
                  sm: "100px",
                  md: "100px",
                  lg: "160px",
                  xl: "170px",
                  "2xl": "170px",
                }}
                width="100%"
                maxW={{
                  base: "80%",
                  sm: "83%",
                  md: "70%",
                  lg: "75%",
                  xl: "65%",
                  "2xl": "50%",
                }}
              >
                <motion.div
                  ref={contentRef}
                  initial="hidden"
                  animate={animationStates.content}
                  variants={slideInVariants}
                >
                  <VStack
                    spacing={{
                      base: 3,
                      sm: 4,
                      md: 5,
                      lg: 6,
                    }}
                    align="center"
                    width="100%"
                  >
                    <Flex
                      align="center"
                      direction="column"
                      gap={0}
                      mt={{
                        base: "10px",
                        sm: "17px",
                        md: "20px",
                        lg: "35px",
                        "2xl": "35px",
                      }}
                      justifyContent="center"
                      width="100%"
                    >
                      <Heading
                        color="white"
                        fontSize={{
                          base: "16px",
                          sm: "20px",
                          md: "24px",
                          lg: "32px",
                          xl: "38px",
                          "2xl": "45px",
                        }}
                        textAlign="center"
                      >
                        <motion.div
                          initial="hidden"
                          animate={animationStates.content}
                          variants={slideInVariants}
                          custom={0}
                        >
                          {TEXTS.Story.content[0]}
                        </motion.div>
                      </Heading>
                      <Heading
                        color="white"
                        fontSize={{
                          base: "12px",
                          sm: "16px",
                          md: "16px",
                          lg: "20px",
                          xl: "25px",
                          "2xl": "25px",
                        }}
                        textAlign="center"
                      >
                        <motion.div
                          initial="hidden"
                          animate={animationStates.content}
                          variants={{
                            ...slideInVariants,
                            visible: {
                              ...slideInVariants.visible,
                              transition: {
                                ...slideInVariants.visible.transition,
                              },
                            },
                          }}
                          custom={1}
                        >
                          {TEXTS.Story.content[1]}
                        </motion.div>
                      </Heading>
                    </Flex>
                    <Box
                      bg="rgba(255, 255, 255, 0.2)"
                      borderRadius="full"
                      mt={{
                        base: "-5px",
                        sm: "-5px",
                        md: "-7px",
                        lg: "-10px",
                        "2xl": "-10px",
                      }}
                      px={{
                        base: 3,
                        sm: 4,
                        md: 5,
                        lg: 6,
                      }}
                      py={{
                        base: 1,
                        sm: 1.5,
                        md: 2,
                        lg: 2.5,
                      }}
                    >
                      <Heading
                        color="rgb(36, 13, 0)"
                        fontSize={{
                          base: "8px",
                          sm: "10px",
                          md: "14px",
                          lg: "20px",
                          xl: "23px",
                          "2xl": "25px",
                        }}
                      >
                        <motion.div
                          initial="hidden"
                          animate={animationStates.content}
                          variants={{
                            ...slideInVariants,
                            visible: {
                              ...slideInVariants.visible,
                              transition: {
                                ...slideInVariants.visible.transition,
                              },
                            },
                          }}
                          custom={2}
                        >
                          {TEXTS.Story.content[2]}
                        </motion.div>
                      </Heading>
                    </Box>
                    <Box
                      as={motion.div}
                      initial="hidden"
                      animate={animationStates.content}
                      variants={{
                        ...slideInVariants,
                        visible: {
                          ...slideInVariants.visible,
                          transition: {
                            ...slideInVariants.visible.transition,
                          },
                        },
                      }}
                      custom={3}
                      mt={{
                        base: "-1",
                        sm: "-2",
                        md: "-3",
                        lg: "-4",
                        xl: "-4",
                        "2xl": "-4",
                      }}
                      mb={{
                        base: 0,
                        sm: 0,
                        md: 0,
                        lg: 0,
                        xl: 0,
                        "2xl": 0,
                      }}
                    >
                      <Text
                        color="rgb(36, 13, 0)"
                        fontSize={{
                          base: "9px",
                          sm: "10px",
                          md: "12px",
                          lg: "16px",
                          xl: "18px",
                          "2xl": "18px",
                        }}
                        fontWeight="bold"
                        textAlign="center"
                        lineHeight="1.2"
                        mt={{
                          base: 0,
                          sm: 0,
                          md: 0,
                          lg: 0,
                          xl: 0,
                          "2xl": 0,
                        }}
                        mb={{
                          base: 0,
                          sm: 0,
                          md: 0,
                          lg: 0,
                        }}
                      >
                        {TEXTS.Story.content[11]}
                      </Text>
                    </Box>
                    <Box
                      w="100%"
                      h={{
                        base: "2px",
                        sm: "2px",
                        md: "2px",
                        lg: "3px",
                        "2xl": "3px",
                      }}
                      bg="white"
                      mt={{
                        base: "-1",
                        sm: "-1",
                        md: "-2",
                        lg: "-3",
                        "2xl": "-3",
                      }}
                    />
                  </VStack>
                </motion.div>
              </Box>

              {/* 下部コンテンツ */}
              <VStack
                spacing={{
                  base: 4,
                  sm: 5,
                  md: 6,
                  lg: 8,
                }}
                width="100%"
              >
                <motion.div
                  ref={bottomContentRef}
                  initial="hidden"
                  animate={animationStates.bottomContent}
                  variants={slideInVariants}
                >
                  <Flex
                    direction={"column"}
                    align="center"
                    gap={{
                      base: 1,
                      sm: 1,
                      md: 2,
                      lg: 2,
                      xl: 3,
                      "2xl": 4,
                    }}
                    justifyContent="center"
                    width="100%"
                    mb={{
                      base: 2,
                      sm: 3,
                      md: 4,
                      lg: 5,
                    }}
                    mt={{
                      base: "-40px",
                      sm: "-45px",
                      md: "-45px",
                      lg: "-70px",
                      "2xl": "-70px",
                    }}
                  >
                    <Heading
                      color="white"
                      fontSize={{
                        base: "10px",
                        sm: "12px",
                        md: "16px",
                        lg: "22px",
                      }}
                      textAlign="center"
                    >
                      <motion.div
                        initial="hidden"
                        animate={animationStates.bottomContent}
                        variants={slideInVariants}
                      >
                        {TEXTS.Story.content[3]}
                      </motion.div>
                    </Heading>
                    <Heading
                      color="white"
                      fontSize={{
                        base: "10px",
                        sm: "12px",
                        md: "16px",
                        lg: "22px",
                      }}
                      textAlign="center"
                      whiteSpace={{
                        base: "pre-line",
                        sm: "pre-line",
                        md: "normal",
                        lg: "normal",
                      }}
                      lineHeight={{
                        base: "1.5",
                        sm: "1.5",
                        md: "normal",
                      }}
                      sx={{
                        "& br, & .chakra-text br": {
                          display: { base: "block", md: "none" },
                          content: { base: '""', md: "none" },
                          marginTop: { base: "-5px", sm: "-5px" },
                        },
                      }}
                    >
                      <motion.div
                        initial="hidden"
                        animate={animationStates.bottomContent}
                        variants={{
                          ...slideInVariants,
                          visible: {
                            ...slideInVariants.visible,
                            transition: {
                              ...slideInVariants.visible.transition,
                            },
                          },
                        }}
                        custom={1}
                      >
                        {TEXTS.Story.content[4]}
                      </motion.div>
                    </Heading>
                  </Flex>
                </motion.div>

                {/* ストーリー画像 */}
                <Box width="100%" textAlign="center">
                  <motion.div
                    ref={storyImageRef}
                    initial="hidden"
                    animate={animationStates.storyImage}
                    variants={{
                      ...slideInVariants,
                      visible: {
                        ...slideInVariants.visible,
                        transition: {
                          duration: 1.0,
                          ease: "easeOut",
                        },
                      },
                    }}
                  >
                    <Image
                      id="story-image"
                      src={StoryImage}
                      alt="story"
                      loading="eager"
                      w={{
                        base: "95%",
                        sm: "90%",
                        md: "85%",
                        lg: "85%",
                        xl: "75%",
                        "2xl": "65%",
                      }}
                      h="auto"
                      mx="auto"
                      maxW={{
                        base: "360px",
                        sm: "450px",
                        md: "700px",
                        lg: "900px",
                        xl: "1200px",
                        "2xl": "1200px",
                      }}
                      {...commonImageProps}
                    />
                  </motion.div>
                </Box>

                {/* テキストセクション */}
                <Box
                  width="100%"
                  textAlign="center"
                  my={{
                    base: 2,
                    sm: 3,
                    md: 4,
                    lg: 5,
                  }}
                >
                  <motion.div
                    ref={textRef}
                    initial="hidden"
                    animate={animationStates.text}
                    variants={textFadeInVariants}
                  >
                    {TEXTS.Story.content.slice(5, 10).map((text, lineIndex) => (
                      <motion.div
                        key={`story-text-${lineIndex}`}
                        initial="hidden"
                        animate={animationStates.text}
                        variants={{
                          hidden: {
                            opacity: 0,
                            x: -30,
                          },
                          visible: {
                            opacity: 1,
                            x: 0,
                            transition: {
                              duration: 0.8,
                              delay: 0.3 + lineIndex * 0.3,
                              ease: "easeOut",
                            },
                          },
                          instant: {
                            opacity: 1,
                            x: 0,
                            transition: {
                              duration: 0,
                            },
                          },
                        }}
                      >
                        <Text
                          color="white"
                          fontSize={{
                            base: "11px",
                            sm: "11px",
                            md: "13px",
                            lg: "17px",
                            "2xl": "22px",
                          }}
                          lineHeight={{
                            base: "1.5",
                            sm: "1.5",
                            md: "1.5",
                            lg: "1.5",
                            xl: "1.5",
                            "2xl": "1.5",
                          }}
                          letterSpacing={{
                            base: "0.02em",
                            sm: "0.02em",
                            md: "normal",
                          }}
                          textAlign="center"
                          mb={{
                            base: "0.3rem",
                            sm: "0.3rem",
                            md: "0.5rem",
                          }}
                          px={{
                            base: "0",
                            sm: "3",
                            md: "0",
                          }}
                          maxW={{
                            base: "95%",
                            sm: "90%",
                            md: "100%",
                          }}
                          mx="auto"
                          whiteSpace={{
                            base: "pre-line",
                            sm: "pre-line",
                            md: "normal",
                            lg: "normal",
                          }}
                          wordBreak={{
                            base: "keep-all",
                            sm: "keep-all",
                            md: "normal",
                          }}
                          overflowWrap={{
                            base: "break-word",
                            sm: "break-word",
                            md: "normal",
                          }}
                        >
                          {text}
                        </Text>
                      </motion.div>
                    ))}
                  </motion.div>
                </Box>

                {/* 画像セクション */}
                <Flex
                  gap={{
                    base: 4,
                    sm: 6,
                    md: 8,
                    lg: 10,
                  }}
                  my={{
                    base: 3,
                    sm: 4,
                    md: 5,
                    lg: 6,
                  }}
                  justify="center"
                  w="100%"
                  direction={{
                    base: "column",
                    md: "row",
                  }}
                  align="center"
                  flexWrap="nowrap"
                >
                  {tgmImages.map((img, index) => (
                    <Box
                      key={img.alt}
                      ref={
                        index === 0
                          ? tgmImage1Ref
                          : index === 1
                          ? tgmImage2Ref
                          : tgmImage3Ref
                      }
                      width={{
                        base: "270px",
                        sm: "270px",
                        md: "200px",
                        lg: "300px",
                        xl: "350px",
                      }}
                      height="auto"
                      overflow="hidden"
                      position="relative"
                      sx={{
                        "&::before": {
                          content: '""',
                          display: "block",
                          paddingTop: "66.67%",
                        },
                      }}
                    >
                      <motion.div
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          transformOrigin: "center",
                        }}
                        initial="hidden"
                        animate={getTgmImageAnimationState(index)}
                        variants={{
                          hidden: {
                            opacity: 0,
                            scale: 1.2,
                          },
                          visible: {
                            opacity: 1,
                            scale: 1,
                            transition: {
                              duration: 1,
                              ease: "easeOut",
                              delay: getImageAnimationDelay(index),
                            },
                          },
                          instant: {
                            opacity: 1,
                            scale: 1,
                            transition: {
                              duration: 0,
                            },
                          },
                        }}
                      >
                        <picture
                          style={{
                            display: "block",
                            width: "100%",
                            height: "100%",
                          }}
                        >
                          <source
                            type="image/avif"
                            srcSet={`/optimized/${img.alt}/${img.alt}-270.avif 270w, /optimized/${img.alt}/${img.alt}-300.avif 300w, /optimized/${img.alt}/${img.alt}-350.avif 350w`}
                            sizes="(min-width: 1200px) 350px, (min-width: 992px) 300px, (min-width: 768px) 200px, 270px"
                          />
                          <source
                            type="image/webp"
                            srcSet={`/optimized/${img.alt}/${img.alt}-270.webp 270w, /optimized/${img.alt}/${img.alt}-300.webp 300w, /optimized/${img.alt}/${img.alt}-350.webp 350w`}
                            sizes="(min-width: 1200px) 350px, (min-width: 992px) 300px, (min-width: 768px) 200px, 270px"
                          />
                          <img
                            src={img.src}
                            alt={img.alt}
                            loading="eager"
                            decoding="async"
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              display: "block",
                            }}
                            onContextMenu={preventDefault}
                            draggable={false}
                          />
                        </picture>
                      </motion.div>
                    </Box>
                  ))}
                </Flex>

                {/* 最後のリンクセクション */}
                <Box
                  width="100%"
                  textAlign="center"
                  mb={{
                    base: 4,
                    sm: 6,
                    md: 8,
                    lg: 10,
                  }}
                >
                  <motion.div
                    ref={lastTextRef}
                    initial="hidden"
                    animate={animationStates.lastText}
                    variants={slideInVariants}
                  >
                    <Box
                      bg="rgba(255, 255, 255, 0.2)"
                      borderRadius="full"
                      px={{
                        base: 4,
                        sm: 6,
                        md: 8,
                        lg: 10,
                      }}
                      py={{
                        base: 2,
                        sm: 2,
                        md: 3,
                        lg: 3,
                      }}
                      width="fit-content"
                      mx="auto"
                    >
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        gap={{
                          base: 2,
                          sm: 3,
                          md: 4,
                          lg: 5,
                        }}
                      >
                        <Text
                          as="a"
                          href="https://paramount.jp/top_gun_maverick/"
                          target="_blank"
                          rel="noopener noreferrer"
                          color="white"
                          fontSize={{
                            base: "10px",
                            sm: "18px",
                            md: "18px",
                            lg: "26px",
                          }}
                          fontWeight="bold"
                          whiteSpace={{
                            base: "normal",
                            sm: "nowrap",
                          }}
                          _hover={{
                            color: "rgb(233, 203, 141)",
                            transition: "color 0.3s ease",
                          }}
                        >
                          {TEXTS.Story.content[10]}
                        </Text>
                      </Box>
                    </Box>
                  </motion.div>
                </Box>
              </VStack>
            </VStack>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Story;
